import * as React from 'react';
import { mapAddOnsToOptions, mapDataWithIdAndNameToOptions } from 'bk-web-app';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  ComboBoxInput,
  TextBlock,
  StyledTextInput,
  toastSuccess,
  toastError,
} from 'bench-internal-theme';
import { useClient } from 'bk-web-app/ClientContext';
import { colors } from 'bench-style-constants';
import { openModal } from 'modal-core-components';
import {
  BulkEditFormLayout,
  BulkEditFormFieldsLayout,
  FormGroup,
  TransactionNameFieldsLayout,
  Report1099FormFieldsLayout,
  UntagButton,
  BulkEditFormActions,
} from './BulkEditForm.styles';
import { useQuery } from '@apollo/client';
import {
  GET_BULK_EDIT_FORM_DATA,
  GET_REFRESHED_LEDGER_BALANCES,
} from 'general-ledger/fast-gl/graphql/queries';
import { BulkEditResponse, GetBulkEditFormData } from 'general-ledger/fast-gl/types';
import { useGeneralLedger } from 'general-ledger/fast-gl/hooks/useGeneralLedger';
import useV2Mutation from 'utils/useV2Mutation';
import {
  BULK_EDIT_MUTATION,
  UNTAG_JOURNAL_ENTRY_CONTRACTORS,
} from 'general-ledger/fast-gl/graphql/mutations';
import ApplyConfirmationModal from 'general-ledger/components/apply-confirmation-modal/ApplyConfirmationModal';
import { VisuallyHidden } from 'general-ledger/fast-gl/NewGeneralLedger.styles';
import { WrappedSpinner } from 'spinner';
import { updateLedgersCachedData } from './cacheOperations';
import { useSelectedEntries } from 'general-ledger/fast-gl/hooks/useSelectedEntries';
import {
  getBulkEditConfirmationDescription,
  getLedgerOrEntriesMessage,
  getUntagConfirmationDescription,
  pluralize,
} from './helpers';
import {
  CREATE_OR_UPDATE_CONTRACTOR,
  GET_CONTRACTOR_LIST,
} from 'general-ledger/components/report-1099-form/graphql';
import useV2Query from 'utils/useV2Query';
import { Source1099 } from 'general-ledger/components/report-1099-form/types';
import { showServerError } from 'bk-web-app';
import AddContractorModal from 'general-ledger/components/add-contractor-modal/AddContractorModal';

type Option = { value: string; label: string };

const editTypeOptions: Option[] = [
  {
    value: 'vendorNamePrefix',
    label: 'Add Prefix',
  },
  {
    value: 'vendorNameSuffix',
    label: 'Add Suffix',
  },
  {
    value: 'vendorName',
    label: 'Replace All',
  },
];

type FormData = {
  addOnId?: Option;
  ledgerAccountId?: Option;
  editType?: Option;
  vendorName?: string;
  contractor?: string | null;
  source?: Option;
  internalNoteBody?: string;
};

export const BulkEditForm = () => {
  const { id } = useClient();
  const { register, handleSubmit, reset, control, formState, setValue } = useForm<FormData>();
  const { search, dateRange } = useGeneralLedger();
  const {
    clearSelectedDataEntryIds,
    clearSelectedLedgerAccountIds,
    selectedDataEntries,
    selectedLedgerAccountIds,
    deselectedDataEntries,
  } = useSelectedEntries();
  const [isContractorSelected, setIsContractorSelected] = React.useState(false);
  const normalizedDeselectedDataEntries = React.useMemo(() => {
    return Array.from(deselectedDataEntries.values()).flatMap(entries => Array.from(entries));
  }, [deselectedDataEntries]);

  const { data, loading, error } = useQuery<GetBulkEditFormData>(GET_BULK_EDIT_FORM_DATA, {
    variables: {
      clientId: id,
    },
  });

  const [untag1099] = useV2Mutation(UNTAG_JOURNAL_ENTRY_CONTRACTORS, {
    onCompleted: data => {
      toastSuccess(
        'Untagging completed successfully',
        `${data.untag1099JournalEntryContractors.numberOfJournalEntriesUntagged} ${pluralize(
          data.untag1099JournalEntryContractors.numberOfJournalEntriesUntagged,
          'transaction was',
          'transactions were',
        )} successfully edited`,
        { position: 'top-right' },
      );
      onReset();
    },
    onError: error => {
      toastError(
        'Failed to untag',
        `There was a problem untagging. The error was: ${error.message}`,
        { position: 'top-right' },
      );
    },
  });

  const [bulkEdit, { loading: bulkEditLoading }] = useV2Mutation(BULK_EDIT_MUTATION, {
    onCompleted: (data: BulkEditResponse) => {
      toastSuccess(
        'Bulk edit completed successfully',
        `${data.bulkEditDataEntries.totalAffectedDataEntries} ${pluralize(
          data.bulkEditDataEntries.totalAffectedDataEntries,
          'entry was',
          'entries were',
        )} successfully edited`,
        { position: 'top-right' },
      );
      onReset();
    },
    update: (cache, { data }, { variables }) => {
      // For ledger account updates, we have to do an update to the cache directly as OpenSearch takes a short amount of time to update.
      // This delay means that we can't simply refetch the ledger account, we have to mask it with a quick cache update.
      if (!data?.bulkEditDataEntries) return;
      if (!variables?.input.ledgerAccountId) return;

      const {
        bulkEditDataEntries: { dataEntries },
      } = data;
      const {
        input: { ledgerAccountId },
      } = variables;

      updateLedgersCachedData(
        cache,
        ledgerAccountId,
        dataEntries,
        Array.from(selectedDataEntries.values()),
      );
    },
    refetchQueries: [
      {
        query: GET_REFRESHED_LEDGER_BALANCES,
        variables: {
          clientId: id,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
        fetchPolicy: 'network-only',
        context: { useV2: true },
      },
    ],
    onError: error => {
      toastError(
        'Failed to bulk edit',
        `There was a problem bulk editing. The error was: ${error.message}`,
        { position: 'top-right' },
      );
    },
  });

  const addOnOptions = React.useMemo(() => {
    if (loading || error || !data) return [];
    return mapAddOnsToOptions(data.addOns);
  }, [data, error, loading]);

  const ledgerAccountOptions = React.useMemo(() => {
    if (loading || error || !data) return [];
    return mapDataWithIdAndNameToOptions(data.ledgerAccounts);
  }, [data, error, loading]);

  const onUntag = () => {
    if (selectedDataEntries.size === 0 && selectedLedgerAccountIds.size === 0) return;
    const selectedDEIds = Array.from(selectedDataEntries.keys()).map(Number);
    const selectedLedgerIds = Array.from(selectedLedgerAccountIds).map(Number);
    const deselectedDEIds = normalizedDeselectedDataEntries.map(Number);
    const untagInput = {
      filter: {
        query: search,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      },
      adhocDataEntriesSelection: selectedDEIds,
      ledgerSelection: selectedLedgerIds,
      dataEntriesNotToBulkEdit: deselectedDEIds,
    };
    openModal(closeModal => (
      <ApplyConfirmationModal
        closeModal={closeModal}
        title="Submission confirmation"
        description={getUntagConfirmationDescription(selectedLedgerIds, selectedDEIds)}
        onConfirm={() => {
          untag1099({
            variables: {
              input: untagInput,
            },
          });
        }}
      />
    ));
  };

  const { data: contractorData, loading: contractorLoading } = useV2Query(GET_CONTRACTOR_LIST, {
    variables: { clientId: id },
    skip: !id,
  });
  const contractor1099Options = contractorData?.contractors?.map(contractorOption => {
    return {
      value: contractorOption.id,
      label: contractorOption.name,
    };
  });
  if (!contractorLoading || !contractor1099Options?.length) {
    contractor1099Options?.push(
      {
        value: 'null',
        label: 'Unknown',
      },
      {
        value: 'Add Contractor',
        label: 'Add Contractor',
      },
    );
  }

  const source1099Options: { value: string | null; label: string }[] = Object.keys(Source1099).map(
    key => {
      return {
        value: key,
        label: Source1099[key],
      };
    },
  );
  source1099Options.push({
    value: 'null',
    label: 'Unknown',
  });

  const onSubmit = (formData: FormData) => {
    if (selectedDataEntries.size === 0 && selectedLedgerAccountIds.size === 0) return;

    const { addOnId, editType, internalNoteBody, ledgerAccountId, vendorName, contractor, source } =
      formData;

    // We represent unknown with a null value which won't work for the check below.
    const contractorValue = contractor === '' ? 'Unknown' : contractor;

    if (
      !addOnId &&
      !ledgerAccountId &&
      !editType &&
      !vendorName &&
      !internalNoteBody &&
      !contractorValue &&
      !source
    ) {
      // Nothing to update.
      // TODO this could be improved with a message to the user but this matches the current behaviour.
      return;
    }

    const selectedDEIds = Array.from(selectedDataEntries.keys()).map(Number);
    const selectedLedgerIds = Array.from(selectedLedgerAccountIds).map(Number);
    const deselectedDEIds = normalizedDeselectedDataEntries.map(Number);

    const bulkEditInput = {
      filter: {
        query: search,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      },
      adhocDataEntriesSelection: selectedDEIds,
      ledgerSelection: selectedLedgerIds,
      dataEntriesNotToBulkEdit: deselectedDEIds,
      addOnId: addOnId?.value ? Number(addOnId.value) : null,
      ledgerAccountId: ledgerAccountId?.value ? Number(ledgerAccountId.value) : null,
      internalNote: internalNoteBody || null,
      [editType?.value as string]: vendorName?.trim() || null,
      contractorId: contractor === 'null' ? null : Number(contractor),
      source1099: source?.value && source?.value !== 'null' ? source.value : null,
    };

    const isTagging1099 = contractor !== undefined || (contractor !== '' && source !== undefined);

    if (isTagging1099) {
      openModal(closeModal => (
        <ApplyConfirmationModal
          closeModal={closeModal}
          title="Submission confirmation"
          description={getBulkEditConfirmationDescription(
            selectedLedgerIds,
            selectedDEIds,
            isTagging1099,
          )}
          onConfirm={() => {
            bulkEdit({
              variables: {
                input: bulkEditInput,
                clientId: id,
                startDate: dateRange?.startDate,
                endDate: dateRange?.endDate,
              },
            });
          }}
        />
      ));
    } else {
      bulkEdit({
        variables: {
          input: bulkEditInput,
          clientId: id,
          startDate: dateRange?.startDate,
          endDate: dateRange?.endDate,
        },
      });
    }
  };

  const onReset = () => {
    reset();
    clearSelectedDataEntryIds && clearSelectedDataEntryIds();
    clearSelectedLedgerAccountIds && clearSelectedLedgerAccountIds();
  };

  const selectionMessage = getLedgerOrEntriesMessage(
    selectedDataEntries.size,
    selectedLedgerAccountIds.size,
    normalizedDeselectedDataEntries.length,
    'has been selected',
    'have been selected',
  );

  const actionsMessage = getLedgerOrEntriesMessage(
    selectedDataEntries.size,
    selectedLedgerAccountIds.size,
    normalizedDeselectedDataEntries.length,
    'will be affected by these changes.',
  );

  // Register 1099 form fields
  register('contractor');
  register('source');

  const [createOrUpdateContractor] = useV2Mutation(CREATE_OR_UPDATE_CONTRACTOR, {
    onError: error => {
      toastError('An error occurred creating this contractor', showServerError(error));
    },
  });

  const handleCreateContractor = ({ name }) => {
    const clientId = id;
    if (!clientId) return Promise.resolve();
    return createOrUpdateContractor({
      variables: {
        input: {
          clientId: parseInt(clientId),
          name,
        },
      },
      refetchQueries: [
        {
          query: GET_CONTRACTOR_LIST,
          variables: { clientId },
          context: { useV2: true },
        },
      ],
      onCompleted: response => {
        toastSuccess('Contractor Created', 'You have successfully created a contractor.');
        setValue('contractor', response.createOrUpdateContractor.id, {
          shouldDirty: true,
          shouldTouch: true,
        });
      },
    });
  };

  const handleContractorSelect = (selectedContractorOption, onChange) => {
    if (selectedContractorOption?.value === 'Add Contractor') {
      openAddContractorDialog();
    } else {
      onChange(selectedContractorOption?.value);
    }
    setIsContractorSelected(!!selectedContractorOption?.value);
  };

  const openAddContractorDialog = () => {
    openModal(closeModal => (
      <AddContractorModal
        closeModal={closeModal}
        title="Add Contractor"
        createContractorData={name => handleCreateContractor({ name })}
      />
    ));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onReset={onReset} name="bulk-edit">
      <BulkEditFormLayout>
        <TextBlock styleType="mediumBold">Bulk Edit</TextBlock>
        <BulkEditFormFieldsLayout>
          <FormGroup>
            <TextBlock as="label" htmlFor="vendorName" styleType="small" uppercase>
              Transaction Name
            </TextBlock>
            <VisuallyHidden>
              <label htmlFor="editType">Edit Type</label>
            </VisuallyHidden>
            <TransactionNameFieldsLayout>
              <Controller
                name="editType"
                control={control}
                defaultValue={editTypeOptions[1]}
                render={({ field: { onChange, onBlur, name, ref } }) => (
                  <ComboBoxInput
                    defaultValue={editTypeOptions[1]}
                    menuPlacement="top"
                    menuPortalTarget={document.body}
                    name={name}
                    inputId={name}
                    onChange={onChange}
                    onBlur={onBlur}
                    options={editTypeOptions}
                    ref={ref}
                  />
                )}
              />
              <StyledTextInput {...register('vendorName')} id="vendorName" />
            </TransactionNameFieldsLayout>
          </FormGroup>
          <FormGroup>
            <TextBlock as="label" htmlFor="ledgerAccountId" styleType="small" uppercase>
              Ledger
            </TextBlock>
            <Controller
              name="ledgerAccountId"
              control={control}
              render={({ field: { name, onChange, onBlur } }) => (
                <ComboBoxInput
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name={name}
                  inputId={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  options={ledgerAccountOptions}
                  placeholder="Select Ledger..."
                />
              )}
            />
          </FormGroup>
          <FormGroup>
            <TextBlock as="label" htmlFor="internalNoteBody" styleType="small" uppercase>
              Internal Note
            </TextBlock>
            <StyledTextInput {...register('internalNoteBody')} id="internalNoteBody" />
          </FormGroup>
          <FormGroup>
            <TextBlock as="label" htmlFor="addOnId" styleType="small" uppercase>
              Add-On
            </TextBlock>
            <Controller
              name="addOnId"
              control={control}
              render={({ field: { name, onChange, onBlur } }) => (
                <ComboBoxInput
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name={name}
                  inputId={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  options={addOnOptions}
                  placeholder="Select Add-On..."
                />
              )}
            />
          </FormGroup>
        </BulkEditFormFieldsLayout>
        <TextBlock styleType="mediumBold">1099</TextBlock>
        <Report1099FormFieldsLayout>
          <FormGroup>
            <TextBlock as="label" htmlFor="contractor" styleType="small" uppercase>
              Contractor
            </TextBlock>
            <Controller
              name="contractor"
              control={control}
              render={({ field: { name, onChange, onBlur, value } }) => (
                <ComboBoxInput
                  key={value}
                  value={contractor1099Options?.find(option => option.value === value)}
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name={name}
                  inputId={name}
                  onChange={data => {
                    handleContractorSelect(data, onChange);
                  }}
                  onBlur={onBlur}
                  options={contractor1099Options}
                  placeholder="Select Contractor..."
                />
              )}
            />
          </FormGroup>
          <FormGroup>
            <TextBlock as="label" htmlFor="source" styleType="small" uppercase>
              Source
            </TextBlock>
            <Controller
              name="source"
              control={control}
              render={({ field: { name, onChange, onBlur, value } }) => (
                <ComboBoxInput
                  key={value}
                  value={value}
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name={name}
                  inputId={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  options={source1099Options}
                  placeholder="Select Source..."
                  isDisabled={!isContractorSelected}
                />
              )}
            />
          </FormGroup>
          <UntagButton type="button" onClick={onUntag}>
            Untag
          </UntagButton>
        </Report1099FormFieldsLayout>
        <BulkEditFormActions>
          <TextBlock color={colors.smoke} styleType="small">
            {formState.isDirty ? actionsMessage : selectionMessage}
          </TextBlock>
          <Button styleType="secondary" type="reset">
            Cancel
          </Button>
          <Button
            data-heap-id="bulk-edit-apply"
            disabled={formState.isSubmitting || !formState.isDirty || bulkEditLoading}
            styleType="primary"
            type="submit"
          >
            Apply
          </Button>
        </BulkEditFormActions>
        {bulkEditLoading && <WrappedSpinner />}
      </BulkEditFormLayout>
    </form>
  );
};
